import { arrayUtils } from '@sato/components';
import { startsWith } from 'lodash';

export const withoutLastPeriod = (t: string) => t.replace(/\.?\s*$/, '');

export const extractTitleFromMarkDown = (markdownText: string) => {
  //regex that matches either on line break or end of the string depending which is shorter
  const re = new RegExp(/.+?(([\r|\t|\n|\r|\r\n|\0])|$)/);
  //remove bolding before making match
  const matches = re.exec(markdownText.replace(/[_|*|#]/g, ''));
  if (matches && matches[0]) {
    return matches[0].trim();
  }
  return null;
};

const trimWithRegex = (regex: RegExp, t: string, limit: number) => {
  let result: string = '';
  let curr: string = '';
  while (withoutLastPeriod(curr).length + 3 < limit) {
    result = curr;
    const match = regex.exec(t);
    if (!match) break;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    curr = curr.concat(match[0]!);
  }
  return withoutLastPeriod(result) + '...';
};

export const trimSentence = (t: string, limit: number): string => {
  const sentenceRegex = new RegExp('([^.]+\\.*)', 'gm');
  return trimWithRegex(sentenceRegex, t, limit);
};

export const trimWord = (t: string, limit: number): string => {
  const wordRegex = new RegExp('(\\S+\\s*)', 'gm');
  return trimWithRegex(wordRegex, t, limit);
};

const _trim = (t: string, upperLimit: number, lowerLimit: number): string => {
  const bySentence = trimSentence(t, upperLimit);
  const byWord =
    bySentence.length < lowerLimit ? trimWord(t, upperLimit) : bySentence;
  return byWord.length < lowerLimit
    ? `${t.substring(0, upperLimit - 3)}...`
    : byWord;
};

export const prettyTrim = (
  t: string,
  limit: number,
  lowerLimit?: number
): string =>
  t &&
  (t.length <= limit
    ? t
    : _trim(t, limit, lowerLimit || Math.floor(limit * (5.0 / 6.0))));

export const isNullOrWhitespace = (input: string) => !input || !input.trim();

/** Trim @start number of characters from the start of the string and @end number of characters from the end */
export const trimString = (t: string, start: number, end?: number) =>
  t.slice(start).slice(0, -(end ?? 0) || undefined);

type MarkdownLinkType = {
  url: string;
  /** User facing text. Defaults to @param url if undefined */
  text?: string;
  /** Tooltip shown when the link is hovered */
  title?: string;
};

export const markdownLink = ({ url, text = url, title }: MarkdownLinkType) => {
  const titlePart = title ? ` "${title}"` : '';

  return `[${text}](${url}${titlePart})`;
};

type MarkdownStyle =
  | 'bold'
  | 'italic'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'code';
type MarkdownList = 'ul' | 'ol';

const stylizeMarkdown = (
  content: string,
  format: MarkdownStyle | MarkdownList
) => {
  switch (format) {
    case 'bold':
      return `**${content}**`;
    case 'italic':
      return `*${content}*`;
    case 'h1':
      return `# ${content}`;
    case 'h2':
      return `## ${content}`;
    case 'h3':
      return `### ${content}`;
    case 'h4':
      return `#### ${content}`;
    case 'h5':
      return `##### ${content}`;
    case 'h6':
      return `###### ${content}`;
    case 'code':
      return `\`\`\`\n${content}\n\`\`\``;
    case 'ul':
      return `- ${content}`;
    // order index doesn't matter in markdown: https://www.markdownguide.org/basic-syntax/
    case 'ol':
      return `1. ${content}`;
  }
};

export const markdownFormat = <T extends string | string[]>(
  text: T,
  format: T extends string[] ? MarkdownList : MarkdownStyle
) => {
  if (Array.isArray(text)) {
    return text.map((item) => stylizeMarkdown(item, format)).join('\n');
  }
  return stylizeMarkdown(text, format);
};

export const markdownParagraphs = (
  paragraphs: Parameters<typeof arrayUtils.joinArrayString>[0]
) => arrayUtils.joinArrayString(paragraphs, '\n\n');

export const ensureHasPrefix = (text: string, prefix: string = '') =>
  startsWith(text, prefix) ? text : prefix + text;

export const requiredSuffix = (text: string) => `${text.trim()} *`;

export const trimLeadingStr = (character: string, text: string) =>
  // always escape the input so that it's correctly regex matched
  text.replace(new RegExp(`^\\${character}?(.*)$`), '$1');
